import type { Space } from '@cocoonspace/sdk-js/types/space'
import { bookingConfig } from '@cocoonspace/shared/config/booking-config'
import { facebookConfig } from '@cocoonspace/shared/config/facebook-config'
import { capitalize } from '@cocoonspace/shared/utils/capitalize'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { SeoTags } from '~/components/seo-tags'
import type { DistrictConfig } from './space-details/space-suggestions'

const twicDomain = process.env.NEXT_PUBLIC_TWICPICS_DOMAIN
const twicPath = process.env.NEXT_PUBLIC_TWICPICS_PATH

export const SpaceSeoTags = ({
	space,
	district,
}: {
	space: Space
	district?: DistrictConfig
}) => {
	const { t } = useTranslation()
	const router = useRouter()
	const city = capitalize(space.address.city)

	const jsonLD = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: t('cwLandings:meetingRooms.metaTitle', { zone: city }),
				item: `https://www.cocoon-space.com/salle-de-reunion/${router.query.zone}`,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: t(`cwDistricts:details.${space.address.postal_code}.title`),
				item: `https://www.cocoon-space.com/salle-de-reunion/${router.query.zone}/${district?.uriParam}`,
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: space.title,
			},
		],
	}

	const spaceTitle = t('space:pageTitle', {
		...space,
		...space.address,
	})

	const spaceDescription = t('space:metaDescription', {
		...space,
		...bookingConfig.hours,
		price_hour: space.prices?.weekday_min?.hour_standard! / 100,
	})

	const twitterMetas = [
		{
			name: 'twitter:label1',
			content: t('space:surface'),
		},
		{
			name: 'twitter:data1',
			content: space.surface,
		},
		{
			name: 'twitter:label2',
			content: t('space:capacity'),
		},
		{
			name: 'twitter:data2',
			content: space.capacity,
		},
	]

	const spaceImage = `${twicDomain}/${twicPath}/${space.images[0]?.path}?twic=v1/resize=${facebookConfig.metaImage.width}`

	return (
		<SeoTags
			title={spaceTitle}
			description={spaceDescription}
			imageUrl={spaceImage}
		>
			<>
				{twitterMetas.map((meta) => (
					<meta
						key={meta.name}
						name={meta.name}
						content={meta.content as string}
					/>
				))}

				<script
					dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
					type='application/ld+json'
				/>
			</>
		</SeoTags>
	)
}
